import { useIsMutating } from "@tanstack/react-query";
import { FC, Suspense, useEffect } from "react";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import paths from "../shared/route/paths";
import { CommonModule } from "../modules/Common/CommonModule";
import { RouterMap } from "../shared/models/RouterMap";
import { AuthenticationModule } from "../modules/Authentication/AuthenticationModule";
import NotFoundPage from "../modules/NotFoundPage";
import { isLoggedInSelector } from "../shared/actions/Authentication.actions";
import { StaffModule } from "../modules/Staff/StaffModule";
import { TodoModule } from "../modules/Todo/TodoModule";
import { CustomerAddModule } from "../modules/CustomerAdd/CustomerAddModule";
import { CustomerProfileModule } from "../modules/CustomerProfile/CustomerProfile.module";
import { CustomerListModule } from "../modules/Customer/CustomerModule";
import { MaintenanceListModule } from "../modules/Maintenance/MaintenanceModule";
import { ExpenseListModule } from "../modules/Expense/ExpenseModule";
import { TodayTaskModule } from "../modules/TodayTask/TodayTaskModule";
import { ConsoleModule } from "../modules/Console/ConsoleModule";
import { AppointmentModule } from "../modules/Appointment/AppointmentModule";
import { ProfileModule } from "../modules/Profile/Profile.module";
import { StockModule } from "../modules/Stock/StockModule";
import { ReceivableListModule } from "../modules/Receivable/ReceivableModule";
import { StatisticsModule } from "../modules/Statistics/StatisticsModule";
import { SupplierModule } from "../modules/Supplier/SupplierModule";
import { BuySellModule } from "../modules/BuySell/BuySellModule";
import { CompanyModule } from "../modules/Company/Company.module";

const Router: FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useRecoilValue(isLoggedInSelector);
  const isFetching = useIsMutating();

  const modules = [
    CommonModule,
    AuthenticationModule,
    StaffModule,
    TodoModule,
    CustomerAddModule,
    CustomerProfileModule,
    CustomerListModule,
    MaintenanceListModule,
    ReceivableListModule,
    ExpenseListModule,
    TodayTaskModule,
    ConsoleModule,
    AppointmentModule,
    ProfileModule,
    StockModule,
    SupplierModule,
    StatisticsModule,
    BuySellModule,
    CompanyModule,
  ];

  const initialList: RouterMap[] = [];

  const privateRoutes =
    (isLoggedIn &&
      modules
        .filter((module) => module.isPrivate)
        .reduce((acc, current) => acc.concat(current.routers), initialList)) ||
    [];

  const publicRoutes =
    modules
      .filter((module) => !module.isPrivate)
      .reduce((acc, current) => acc.concat(current.routers), initialList) || [];

  useEffect(() => {
    if (!isLoggedIn) {
      navigate(paths.AUTHENTICATION.LOGIN);
    }
  }, []);

  return (
    <Suspense fallback={<></>}>
      {/* {!!isFetching && <div>Loading Component</div>} */}

      <Routes>
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />

        {privateRoutes.map(({ path, component }) => (
          <Route
            key={`private-route-${path}`}
            Component={component}
            path={path}
          />
        ))}

        {publicRoutes.map(({ path, component }) => (
          <Route
            key={`public-route-${path}`}
            Component={component}
            path={path}
          />
        ))}
      </Routes>
    </Suspense>
  );
};

export default Router;
